import React, { useState, useRef, useEffect, useId } from "react";
import Cropper from "react-cropper";
import "cropperjs/dist/cropper.css";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, Button,
  CircularProgress, Typography, Alert, IconButton, Stack,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

const ImageCropperModal = ({
  open, onClose, aspectRatio = 1, maxOutputWidth = 1080,
  compressionQuality = 0.85, uploadUrl = "/api/upload", headers = {},
  onSuccess, onError, imageLabel = "profile photo",
}) => {
  const cropperRef = useRef(null);
  const fileRef = useRef(null);
  const selectionRef = useRef(0);
  const previewId = `crop-preview-${useId().replace(/:/g, "")}`;
  const titleId = `${previewId}-title`;
  const descriptionId = `${previewId}-description`;
  const [imageSrc, setImageSrc] = useState(null);
  const [fileInfo, setFileInfo] = useState(null);
  const [ready, setReady] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const isSquare = aspectRatio === 1;
  const outputHeight = Math.round(maxOutputWidth / aspectRatio);

  useEffect(() => {
    if (!open) {
      selectionRef.current += 1;
      setImageSrc(null);
      setFileInfo(null);
      setReady(false);
      setError(null);
    }
  }, [open]);

  useEffect(() => () => { selectionRef.current += 1; }, []);

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    const selection = ++selectionRef.current;
    setError(null);
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      setError("Choose a JPG, PNG or WebP image.");
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      setError("This image is too large. Choose a file under 50 MB.");
      return;
    }
    const reader = new FileReader();
    const fail = () => {
      if (selection === selectionRef.current) {
        setError("We couldn't read this image. Please choose another file.");
      }
    };
    reader.onerror = fail;
    reader.onload = () => {
      const img = new Image();
      img.onerror = fail;
      img.onload = () => {
        if (selection !== selectionRef.current) return;
        setReady(false);
        setFileInfo({ name: file.name, width: img.width, height: img.height });
        setImageSrc(reader.result);
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  };

  const getCroppedImageBlob = () => new Promise((resolve, reject) => {
    const cropper = cropperRef.current?.cropper;
    if (!cropper || !ready) {
      reject(new Error("The image is still loading. Please try again."));
      return;
    }
    try {
      const crop = cropper.getData();
      const canvas = cropper.getCroppedCanvas({
        maxWidth: maxOutputWidth,
        maxHeight: outputHeight,
        width: Math.min(maxOutputWidth, Math.max(1, Math.round(crop.width))),
        fillColor: "#ffffff",
        imageSmoothingEnabled: true,
        imageSmoothingQuality: "high",
      });
      if (!canvas) throw new Error("Unable to prepare the image. Try resetting the crop.");
      canvas.toBlob((blob) => {
        if (blob) resolve(blob);
        else reject(new Error("Unable to prepare the image. Please try another file."));
      }, "image/jpeg", compressionQuality);
    } catch (err) {
      reject(err);
    }
  });

  const handleUpload = async () => {
    if (loading || !ready) return;
    setLoading(true);
    setError(null);
    try {
      const blob = await getCroppedImageBlob();
      const formData = new FormData();
      formData.append("image", blob, "cropped-image.jpg");
      const response = await fetch(uploadUrl, { method: "POST", headers, body: formData });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.message || data.error || "Upload failed. Please try again.");
      }
      onSuccess?.(data);
      onClose();
    } catch (err) {
      setError(err.message);
      onError?.(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} fullWidth maxWidth="md"
      aria-labelledby={titleId} aria-describedby={descriptionId}
      PaperProps={{ sx: { borderRadius: 3, m: { xs: 1, sm: 3 }, width: { xs: "calc(100% - 16px)", sm: "100%" } } }}>
      <DialogTitle id={titleId} sx={{ pr: 7 }}>
        Update {imageLabel}
        <IconButton aria-label="Close image editor" onClick={onClose} disabled={loading}
          sx={{ position: "absolute", top: 12, right: 12 }}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box id={descriptionId} sx={{ mb: 2, p: 2, bgcolor: "#f1f5f9", borderRadius: 2 }}>
          <Typography fontWeight={700}>
            Recommended: {maxOutputWidth} × {outputHeight} px · {isSquare ? "Square (1:1)" : `${aspectRatio}:1 landscape`}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {isSquare ? "Keep your face or logo centered. Profile photos appear in a circle." : "Keep important details near the center. Banner edges may be cropped on smaller screens."}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            JPG, PNG or WebP · Up to 50 MB · Saved as JPG, up to {maxOutputWidth} × {outputHeight} px. Smaller images keep their resolution.
          </Typography>
        </Box>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <input ref={fileRef} aria-label={`Choose ${imageLabel}`} accept="image/jpeg,image/png,image/webp"
          style={{ display: "none" }} type="file" onChange={handleFileSelect} disabled={loading} />
        {!imageSrc ? (
          <Box sx={{ textAlign: "center", p: { xs: 3, sm: 5 }, border: "2px dashed #cbd5e1", borderRadius: 2 }}>
            <CloudUploadIcon sx={{ fontSize: 40, color: "text.secondary", mb: 1 }} />
            <Typography sx={{ mb: 2 }}>Choose an image, then adjust the crop before saving.</Typography>
            <Button variant="contained" onClick={() => fileRef.current?.click()}>Choose image</Button>
          </Box>
        ) : (
          <>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ height: { xs: 260, sm: 360 }, overflow: "hidden", bgcolor: "#0f172a", borderRadius: 2 }}>
                  <Cropper key={imageSrc} ref={cropperRef} src={imageSrc} aspectRatio={aspectRatio}
                    initialAspectRatio={aspectRatio} viewMode={1} autoCropArea={0.9}
                    dragMode="move" responsive background={false} preview={`#${previewId}`}
                    ready={() => setReady(true)} style={{ height: "100%", width: "100%" }} />
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Drag to reposition. Resize the crop box or use the zoom controls.
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 1 }}>
                  <Button size="small" disabled={!ready || loading} onClick={() => cropperRef.current?.cropper.zoom(-0.1)}>Zoom out</Button>
                  <Button size="small" disabled={!ready || loading} onClick={() => cropperRef.current?.cropper.zoom(0.1)}>Zoom in</Button>
                  <Button size="small" disabled={!ready || loading} onClick={() => cropperRef.current?.cropper.reset()}>Reset crop</Button>
                </Stack>
              </Box>
              <Box sx={{ width: { xs: "100%", sm: 200 }, flexShrink: 0 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Live preview</Typography>
                <Box id={previewId} role="img" aria-label={`${imageLabel} crop preview`}
                  sx={{ width: "100%", maxWidth: isSquare ? 160 : 240, aspectRatio: `${aspectRatio}`, overflow: "hidden", borderRadius: isSquare ? "50%" : 1, bgcolor: "#e2e8f0" }} />
                <Typography variant="body2" sx={{ mt: 2, overflowWrap: "anywhere" }}>{fileInfo?.name}</Typography>
                <Typography variant="caption" color="text.secondary">Original: {fileInfo?.width} × {fileInfo?.height} px</Typography>
              </Box>
            </Stack>
            {fileInfo && (fileInfo.width < maxOutputWidth || fileInfo.height < outputHeight) && (
              <Alert severity="info" sx={{ mt: 2 }}>This image is smaller than recommended. You can still use it, but it may look less sharp.</Alert>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1, flexWrap: "wrap" }}>
        {imageSrc && <Button disabled={loading} onClick={() => fileRef.current?.click()}>Choose another image</Button>}
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button variant="contained" onClick={handleUpload} disabled={!imageSrc || !ready || loading}
          startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}>
          {loading ? "Saving…" : `Save ${imageLabel}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImageCropperModal;
